import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '../../contexts/TenantContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Download, AlertTriangle, ShieldCheck, FileArchive, Clock, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

import { apiWorker } from '../../api/worker';
import { safeFormat } from '../../lib/utils';

interface ExportItem {
  id: string;
  type: string;
  targetId: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  expiresAt?: string;
}

export const DataSettingsPage = () => {
  const { node } = useTenant();
  const queryClient = useQueryClient();
  const [isExporting, setIsExporting] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { data: exportHistory = [], isLoading: isLoadingHistory } = useQuery<ExportItem[]>({
    queryKey: ['node-export-history', node?.id],
    queryFn: async () => {
      if (!node?.id) return [];
      const res = await apiWorker.get(`/api/nodes/${node.id}/export/status`);
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: !!node?.id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (Array.isArray(data) && data.some(item => item.status === 'PENDING' || item.status === 'PROCESSING')) {
        return 3000;
      }
      return false;
    }
  });

  const handleRequestExport = async () => {
    if (!node?.id) return;
    setIsExporting(true);
    try {
      await apiWorker.post(`/api/nodes/${node.id}/export`);
      toast.success('Data export started. Check the history below for status.');
      queryClient.invalidateQueries({ queryKey: ['node-export-history', node.id] });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to start data export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownload = async (exportId: string) => {
    if (!node?.id) return;
    setDownloadingId(exportId);
    try {
      const response = await apiWorker.get(`/api/nodes/${node.id}/export/${exportId}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `org_${node.slug || node.id}_export_${exportId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Download started.');
    } catch (err) {
      toast.error('Failed to download export file. It may have expired.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
          Back
        </Button>
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">Data & Compliance</h1>
          <p className="text-xs md:text-sm text-gray-500">Manage your organization's data exports and retention policies.</p>
        </div>
      </div>

      <Card className="space-y-6">
        <div className="flex items-center gap-3 border-b border-black dark:border-white pb-4 dark:border-white">
          <div className="p-3 bg-blue-50 text-blue-700 rounded-none dark:bg-blue-900/30 dark:text-blue-400">
            <FileArchive size={24} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">Organization Data Export</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Download a complete archive of all organizational data.</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-blue-50 border-2 border-black dark:border-white border-blue-100 rounded-none flex gap-3 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800/30 dark:text-blue-300">
            <ShieldCheck size={20} className="shrink-0" />
            <p className="text-sm leading-relaxed">
              Exporting data will aggregate your members, configuration, files, and ledger history. The generated archive will be available to download for 7 days before being automatically deleted.
            </p>
          </div>
          
          <Button 
            onClick={handleRequestExport}
            isLoading={isExporting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Download size={16} className="mr-2" />
            Request Data Export
          </Button>
        </div>
      </Card>

      <Card className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-bold text-gray-900 dark:text-white">Export History</h4>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => queryClient.invalidateQueries({ queryKey: ['node-export-history', node?.id] })}
            className="text-xs"
          >
            <RefreshCw size={12} className="mr-1" />
            Refresh
          </Button>
        </div>
        
        {isLoadingHistory ? (
          <div className="p-8 text-center text-gray-400">
            <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
            <p className="text-xs">Loading export records...</p>
          </div>
        ) : exportHistory.length === 0 ? (
          <div className="bg-white rounded-none border-2 border-black dark:border-white border-dashed border-black dark:border-white dark:bg-slate-950 dark:border-white p-8 text-center">
            <Clock size={32} className="mx-auto text-gray-300 dark:text-slate-700 mb-2" />
            <p className="text-sm text-gray-500 font-medium">No previous exports found.</p>
            <p className="text-xs text-gray-400 mt-1">Requested archives will appear here once generated.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-slate-800 border-2 border-black dark:border-white">
            {exportHistory.map((item) => (
              <div key={item.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-gray-900 dark:text-white">
                      Archive #{item.id.substring(0, 8)}
                    </span>
                    {item.status === 'COMPLETED' && <Badge variant="green">Ready</Badge>}
                    {item.status === 'PROCESSING' && <Badge variant="blue">Processing</Badge>}
                    {item.status === 'PENDING' && <Badge variant="amber">Queued</Badge>}
                    {item.status === 'FAILED' && <Badge variant="red">Failed</Badge>}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Requested on {safeFormat(item.createdAt, 'PPpp')}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {item.status === 'COMPLETED' && (
                    <Button
                      size="sm"
                      onClick={() => handleDownload(item.id)}
                      isLoading={downloadingId === item.id}
                      className="bg-black text-white hover:bg-navy-800 text-xs gap-1.5"
                    >
                      <Download size={14} />
                      Download ZIP
                    </Button>
                  )}
                  {item.status === 'PROCESSING' && (
                    <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                      <RefreshCw size={12} className="animate-spin" /> Compiling archive...
                    </span>
                  )}
                  {item.status === 'PENDING' && (
                    <span className="text-xs text-amber-600 dark:text-amber-400">
                      In Queue...
                    </span>
                  )}
                  {item.status === 'FAILED' && (
                    <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                      <XCircle size={12} /> Generation failed
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4 bg-amber-50 dark:bg-amber-950/20 border-2 border-black dark:border-white border-amber-100 dark:border-amber-900/30 rounded-none flex gap-3 text-amber-800 dark:text-amber-500 mt-4">
        <AlertTriangle size={20} className="shrink-0 mt-0.5" />
        <div className="text-xs space-y-1">
          <h5 className="font-bold text-amber-900 dark:text-amber-400">Security Warning</h5>
          <p className="leading-relaxed text-amber-700 dark:text-amber-500/80">
            Exported data contains highly sensitive Personally Identifiable Information (PII) of your members. Ensure you store downloaded archives securely and in compliance with your local data protection regulations (e.g., GDPR, CCPA).
          </p>
        </div>
      </Card>
    </div>
  );
};
